import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import { useWallet } from "@/context/WalletContext";
import {
  Coins,
  TrendingUp,
  TrendingDown,
  ShieldAlert,
  Ban,
  Loader2,
  Copy,
  Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMicro(v) {
  const n = (Number(v) || 0) / 1_000_000;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtSigned(v) {
  const n = (Number(v) || 0) / 1_000_000;
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function trunc(a, n = 8) {
  if (!a) return "—";
  return `${a.slice(0, n)}…${a.slice(-4)}`;
}
async function copyText(t) {
  try { await navigator.clipboard.writeText(t); toast.success("Copied"); } catch { /* noop */ }
}

/**
 * AdminFlip — Admin → Flip tab.
 *  1. Totals (net winners/losers, suspicious, volume)
 *  2. Suspicious wallets (statistically improbable win frequency) with quick-block
 *  3. Winners board / Losers board
 *  4. Wallet blocklist manager (block deposit/withdrawal site-wide, with note)
 */
export default function AdminFlip() {
  const { getAuthHeaders, isConnected } = useWallet();
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ address: "", block_deposit: true, block_withdrawal: true, note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [pl, bl] = await Promise.all([
        fetch(`${API}/api/admin/flip/players`, { headers }),
        fetch(`${API}/api/admin/blocked-wallets`, { headers }),
      ]);
      if (pl.ok) setData(await pl.json());
      if (bl.ok) setBlocked((await bl.json()).blocked || []);
    } catch (e) {
      console.error("admin flip load error", e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isConnected) { setLoading(false); return; }
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isConnected, load]);

  const submitBlock = async (payload) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/blocked-wallets`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        toast.success(`Blocked ${trunc(payload.address, 8)}`);
        setForm({ address: "", block_deposit: true, block_withdrawal: true, note: "" });
        load();
      } else {
        toast.error(j.detail || "Failed to block");
      }
    } catch { toast.error("Block error"); }
    finally { setSaving(false); }
  };

  const unblock = async (address) => {
    if (!window.confirm(`Unblock ${trunc(address, 10)}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/blocked-wallets/${address}`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (res.ok) { toast.success("Unblocked"); load(); }
      else toast.error("Failed to unblock");
    } catch { toast.error("Unblock error"); }
  };

  const quickBlock = (p) => {
    submitBlock({
      address: p.address,
      block_deposit: true,
      block_withdrawal: true,
      note: `Flip gaming: ${(p.reasons || []).join("; ") || "suspicious win pattern"} (${p.z_score}σ)`,
    });
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <Coins className="w-6 h-6 text-orange-500" /> Flip Admin
          </h1>
          <AdminTabsNav active="flip" />
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center text-zinc-400" data-testid="admin-flip-connect">
              Connect your admin wallet to view Flip analytics.
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};

  const PlayerRow = ({ p, showBlock }) => (
    <tr className="border-b border-zinc-800/50" data-testid={`flip-player-row-${p.address}`}>
      <td className="py-2 pr-2">
        <button className="font-mono text-xs text-zinc-300 inline-flex items-center gap-1 hover:text-white"
          onClick={() => copyText(p.address)}>
          {trunc(p.address, 8)} <Copy className="w-3 h-3" />
        </button>
      </td>
      <td className="py-2 pr-2 text-zinc-400">{p.bets}</td>
      <td className="py-2 pr-2">{p.win_rate}%</td>
      <td className={`py-2 pr-2 font-semibold ${p.net_stxoshi >= 0 ? "text-green-400" : "text-red-400"}`}>
        {fmtSigned(p.net_stxoshi)}
      </td>
      <td className="py-2 pr-2 text-zinc-400">{p.z_score}σ</td>
      {showBlock && (
        <td className="py-2 pr-2">
          {p.blocked ? (
            <Badge variant="outline" className="text-red-400 border-red-500/40">Blocked</Badge>
          ) : (
            <Button size="sm" className="h-7 bg-red-600 hover:bg-red-700"
              onClick={() => quickBlock(p)} data-testid={`flip-quickblock-${p.address}`}>
              <Ban className="w-3 h-3 mr-1" /> Block
            </Button>
          )}
        </td>
      )}
    </tr>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-flip-page">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Coins className="w-6 h-6 text-orange-500" /> Flip Market Admin
        </h1>
        <AdminTabsNav active="flip" />

        {loading && !data ? (
          <div className="flex items-center gap-2 text-zinc-400 py-10">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Players</p>
                <p className="text-xl font-bold" data-testid="flip-total-players">{totals.total_players || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Net Winners</p>
                <p className="text-xl font-bold text-green-400" data-testid="flip-total-winners">{totals.net_winners || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Net Losers</p>
                <p className="text-xl font-bold text-red-400" data-testid="flip-total-losers">{totals.net_losers || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Suspicious</p>
                <p className="text-xl font-bold text-amber-400" data-testid="flip-total-suspicious">{totals.suspicious_count || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Volume Staked</p>
                <p className="text-lg font-bold">{fmtMicro(totals.total_staked_stxoshi)}</p>
              </CardContent></Card>
            </div>

            {/* Suspicious */}
            <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="admin-flip-suspicious">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShieldAlert className="w-5 h-5 text-amber-500" /> Suspicious Wallets
                  <Badge variant="outline" className="ml-2">{data?.suspicious?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {(data?.suspicious || []).length === 0 ? (
                  <p className="text-sm text-zinc-500 py-3" data-testid="flip-suspicious-empty">No statistically suspicious wallets detected.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-zinc-500 text-xs border-b border-zinc-800">
                      <th className="py-2 pr-2">Wallet</th><th className="py-2 pr-2">Bets</th>
                      <th className="py-2 pr-2">Win%</th><th className="py-2 pr-2">Net</th>
                      <th className="py-2 pr-2">σ</th><th className="py-2 pr-2">Action</th>
                    </tr></thead>
                    <tbody>{data.suspicious.map((p) => <PlayerRow key={p.address} p={p} showBlock />)}</tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Winners */}
              <Card className="bg-zinc-900 border-zinc-800" data-testid="admin-flip-winners">
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-green-400" /> Winning Wallets
                </CardTitle></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-zinc-500 text-xs border-b border-zinc-800">
                      <th className="py-2 pr-2">Wallet</th><th className="py-2 pr-2">Bets</th>
                      <th className="py-2 pr-2">Win%</th><th className="py-2 pr-2">Net</th><th className="py-2 pr-2">σ</th>
                    </tr></thead>
                    <tbody>{(data?.winners || []).map((p) => <PlayerRow key={p.address} p={p} />)}</tbody>
                  </table>
                  {(data?.winners || []).length === 0 && <p className="text-sm text-zinc-500 py-3">No net-winning wallets.</p>}
                </CardContent>
              </Card>

              {/* Losers */}
              <Card className="bg-zinc-900 border-zinc-800" data-testid="admin-flip-losers">
                <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingDown className="w-5 h-5 text-red-400" /> Losing Wallets
                </CardTitle></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-zinc-500 text-xs border-b border-zinc-800">
                      <th className="py-2 pr-2">Wallet</th><th className="py-2 pr-2">Bets</th>
                      <th className="py-2 pr-2">Win%</th><th className="py-2 pr-2">Net</th><th className="py-2 pr-2">σ</th>
                    </tr></thead>
                    <tbody>{(data?.losers || []).map((p) => <PlayerRow key={p.address} p={p} />)}</tbody>
                  </table>
                  {(data?.losers || []).length === 0 && <p className="text-sm text-zinc-500 py-3">No net-losing wallets.</p>}
                </CardContent>
              </Card>
            </div>

            {/* Blocklist manager */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="admin-flip-blocklist">
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-lg">
                <Ban className="w-5 h-5 text-red-500" /> Wallet Blocklist
                <Badge variant="outline" className="ml-2">{blocked.length}</Badge>
              </CardTitle></CardHeader>
              <CardContent>
                {/* Add block form */}
                <div className="border border-zinc-800 rounded-lg p-3 mb-4 space-y-2">
                  <Input placeholder="Wallet address (SP…)" value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 font-mono text-xs"
                    data-testid="flip-block-address-input" />
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.block_deposit}
                        onChange={(e) => setForm({ ...form, block_deposit: e.target.checked })}
                        data-testid="flip-block-deposit-check" />
                      Block Deposit
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.block_withdrawal}
                        onChange={(e) => setForm({ ...form, block_withdrawal: e.target.checked })}
                        data-testid="flip-block-withdrawal-check" />
                      Block Withdrawal
                    </label>
                  </div>
                  <Input placeholder="Note — why is this wallet blocked?" value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="flip-block-note-input" />
                  <Button className="bg-red-600 hover:bg-red-700" disabled={saving || !form.address}
                    onClick={() => submitBlock(form)} data-testid="flip-block-submit-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                    Block Wallet
                  </Button>
                </div>

                {/* Existing blocks */}
                {blocked.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-2" data-testid="flip-blocklist-empty">No wallets currently blocked.</p>
                ) : (
                  <div className="space-y-2">
                    {blocked.map((b) => (
                      <div key={b.address} className="flex flex-wrap items-center justify-between gap-2 border border-zinc-800 rounded-lg p-2" data-testid={`flip-blocked-row-${b.address}`}>
                        <div className="min-w-0">
                          <button className="font-mono text-xs text-zinc-300 inline-flex items-center gap-1 hover:text-white"
                            onClick={() => copyText(b.address)}>
                            {trunc(b.address, 10)} <Copy className="w-3 h-3" />
                          </button>
                          <div className="flex gap-1 mt-1">
                            {b.block_deposit && <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">no deposit</Badge>}
                            {b.block_withdrawal && <Badge variant="outline" className="text-[10px] text-red-400 border-red-500/30">no withdrawal</Badge>}
                          </div>
                          {b.note && <p className="text-xs text-zinc-500 mt-1">{b.note}</p>}
                          <p className="text-[10px] text-zinc-600 mt-0.5">
                            {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 border-zinc-700"
                          onClick={() => unblock(b.address)} data-testid={`flip-unblock-${b.address}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
